import { NavLink } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBellConcierge,
  faHandHoldingDollar,
  faHouse,
  faMoneyBillTransfer,
  faPiggyBank,
  faRightFromBracket,
  faSeedling,
  faUser,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import Avatar from '@/components/ui/Avatar/Avatar';
import { formatDateTime } from '@/utils/formatter';
import { DASHBOARD_LABELS } from '@/constants/static/dashboardLabels';
import type { UserProfile } from '@/types/account.types';

interface SidebarProps {
  user: UserProfile | null;
  userError: string | null;
  loading: boolean;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  onRetryUserProfile: () => void;
}

const navItems = [
  { to: '/dashboard', label: DASHBOARD_LABELS.sidebar.nav.home, icon: faHouse, comingSoon: false },
  { to: '/pay', label: DASHBOARD_LABELS.sidebar.nav.pay, icon: faMoneyBillTransfer, comingSoon: false },
  { to: '/services', label: DASHBOARD_LABELS.sidebar.nav.services, icon: faBellConcierge, comingSoon: false },
  { to: '/save', label: DASHBOARD_LABELS.sidebar.nav.save, icon: faPiggyBank, comingSoon: true },
  { to: '/invest', label: DASHBOARD_LABELS.sidebar.nav.invest, icon: faSeedling, comingSoon: true },
  { to: '/borrow', label: DASHBOARD_LABELS.sidebar.nav.borrow, icon: faHandHoldingDollar, comingSoon: false },
  { to: '/profile', label: DASHBOARD_LABELS.sidebar.nav.profile, icon: faUser, comingSoon: false },
];

const Sidebar = ({ user, userError, loading, isOpen, onClose, onLogout, onRetryUserProfile }: SidebarProps) => {
  return (
    <>
      <div
        className={`nova-sidebar-backdrop ${isOpen ? 'show' : ''}`}
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <aside
        id="nova-sidebar"
        className={`nova-sidebar ${isOpen ? 'open' : ''}`}
        role="navigation"
        aria-label={DASHBOARD_LABELS.sidebar.navigationAriaLabel}
      >
        <div className="nova-sidebar-header">
          <NavLink to="/dashboard" className="nova-sidebar-logo" onClick={onClose}>
            <span className="nova-logo-mark">{DASHBOARD_LABELS.app.logoMark}</span>
            <span className="nova-logo-text">{DASHBOARD_LABELS.app.name}</span>
          </NavLink>
          {isOpen && (
            <button
              className="nova-sidebar-close"
              onClick={onClose}
              aria-label={DASHBOARD_LABELS.sidebar.closeAriaLabel}
              type="button"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>

       

        <nav className="nova-sidebar-nav">
          <ul className="nova-sidebar-menu">
            {navItems.map((item) => (
              <li key={item.to}>
                {item.comingSoon ? (
                  <button
                    type="button"
                    className="nova-sidebar-link nova-sidebar-link-disabled"
                    title={DASHBOARD_LABELS.sidebar.comingSoon}
                    aria-label={`${item.label} (${DASHBOARD_LABELS.sidebar.comingSoon})`}
                  >
                    <FontAwesomeIcon icon={item.icon} className="nova-sidebar-icon" />
                    <span>{item.label}</span>
                    <span className="nova-sidebar-badge">{DASHBOARD_LABELS.sidebar.comingSoon}</span>
                  </button>
                ) : (
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `nova-sidebar-link ${isActive ? 'active' : ''}`}
                    onClick={onClose}
                  >
                    <FontAwesomeIcon icon={item.icon} className="nova-sidebar-icon" />
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

         <div className="nova-sidebar-profile">
          {loading && !user ? (
            <div className="nova-sidebar-profile-skeleton">
              <div className="nova-skeleton-avatar" />
              <div className="nova-skeleton-lines">
                <div className="nova-skeleton-line" />
                <div className="nova-skeleton-line short" />
              </div>
            </div>
          ) : userError && !user ? (
            <div className="nova-sidebar-profile-error">
              <p className="mb-1">{DASHBOARD_LABELS.sidebar.profileLoadError}</p>
              <button className="nova-sidebar-retry-btn" onClick={onRetryUserProfile} type="button">
                {DASHBOARD_LABELS.sidebar.retry}
              </button>
            </div>
          ) : (
            <>
              <Avatar name={user?.fullName || DASHBOARD_LABELS.hero.guestName} src={user?.profilePhotoUrl} size="lg" />
              <div className="nova-sidebar-profile-info">
                <span className="nova-sidebar-profile-name">{user?.fullName || user?.username || DASHBOARD_LABELS.hero.guestName}</span>
                <span className="nova-sidebar-profile-login">
                  {DASHBOARD_LABELS.sidebar.lastLoginLabel} {user?.lastLoginAt ? formatDateTime(user.lastLoginAt) : DASHBOARD_LABELS.sidebar.lastLoginDefault}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="nova-sidebar-footer">
          <button
            className="nova-sidebar-link nova-logout-link"
            onClick={onLogout}
            type="button"
            aria-label={DASHBOARD_LABELS.sidebar.logoutAriaLabel}
          >
            <FontAwesomeIcon icon={faRightFromBracket} className="nova-sidebar-icon" />
            <span>{DASHBOARD_LABELS.sidebar.logoutLabel}</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
